import React, { useEffect, useMemo } from 'react';
import { isToday, parseISO, format } from 'date-fns';
import { useAvailabilityReports, useAvailabilityReportDetail } from '@/hooks/availability-report';
import { useAvailabilityReportStore } from '@/stores';
import {
  WaitingState,
  HistoricalBanner,
} from '@/features/recall-report/components';
import { AvailabilityReportView } from './AvailabilityReportView';
import { TourSentinel } from '@/features/onboarding';

interface AvailabilityReportTabProps {
  counterparty: string;
  searchQuery?: string;
}

export const AvailabilityReportTab: React.FC<AvailabilityReportTabProps> = ({ counterparty, searchQuery }) => {
  const selectedDate = useAvailabilityReportStore((state) => state.selectedDate);
  const setSelectedDate = useAvailabilityReportStore((state) => state.setSelectedDate);
  const selectedReportId = useAvailabilityReportStore((state) => state.selectedReportId);
  const setSelectedReportId = useAvailabilityReportStore((state) => state.setSelectedReportId);

  // Primary data: reports filtered by counterparty on the server
  const { data: rawReports = [] } = useAvailabilityReports({ counterparty });
  const reports = useMemo(() => rawReports.filter((r) => r != null), [rawReports]);

  const todayReport = useMemo(
    () => reports.find((r) => isToday(parseISO(r.created_at))),
    [reports],
  );

  const hasTodayReport = !!todayReport;

  // Poll when waiting for today's report
  useAvailabilityReports({
    counterparty,
    refetchInterval: !selectedDate && !hasTodayReport ? 5 * 60 * 1000 : false,
  });

  const dateReport = useMemo(() => {
    if (!selectedDate) return null;
    return reports.find(
      (r) => format(parseISO(r.created_at), 'yyyy-MM-dd') === selectedDate,
    );
  }, [reports, selectedDate]);

  const activeReport = selectedDate ? dateReport : todayReport;

  // Auto-select report when active report changes
  useEffect(() => {
    if (activeReport) {
      setSelectedReportId(activeReport.id);
    } else {
      setSelectedReportId(null);
    }
  }, [activeReport, setSelectedReportId]);

  const {
    data: reportDetail,
    isLoading: isDetailLoading,
    error: detailError,
  } = useAvailabilityReportDetail(selectedReportId);

  const showWaitingState = !selectedDate && !hasTodayReport;

  return (
    <div className="relative flex h-full flex-col">
      <TourSentinel name="cpty-availability-overview" />
      {/* Report content */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {showWaitingState ? (
          <WaitingState />
        ) : (
          <>
            {selectedDate && (
              <HistoricalBanner
                date={format(parseISO(selectedDate), 'dd MMM yyyy')}
                onBackToToday={() => setSelectedDate(null)}
              />
            )}
            <AvailabilityReportView
              report={reportDetail}
              isLoading={isDetailLoading}
              error={detailError}
              searchQuery={searchQuery}
            />
          </>
        )}
      </div>
    </div>
  );
};
